import { getIsLocalHost } from "@pathwright/features/src/modules/lib/env"
import { getQueryObject } from "@pathwright/features/src/modules/utils/urls"
import omit from "lodash/omit"
import { Route } from "react-router-dom"
import { canGoHome } from "../../../belltower/utils"
import { hasStoreEnabled } from "../../../store/utils"
import { getSchoolQueryParam, transoformLocalConfigURL } from "../api/urls"
import AppRoute from "./AppRoute"
import { getRouteKind, registerRouteTable } from "./routeRegistry"

export const historyListener = function (location) {
  const legacyLocation = getLegacyRouterLocation(location)

  // Track last_pathname
  window.localStorage.setItem("last_pathname", legacyLocation.pathname)
  window.App.getStore("navigation").action.setLocation(legacyLocation)
  window.App.getStore("analytics").action.trackPageView(legacyLocation.pathname)
  window.App.getStore("auth").action.clearNextUrl()
}

export const getLegacyRouterLocation = (location) => {
  const legacyRouterState = {
    ...location,
    query: getQueryObject(location.search)
  }

  return legacyRouterState
}

export const getLegacyRouterState = (nextState) => {
  const legacyRouterState = {
    ...nextState,
    params: nextState.match.params,
    location: getLegacyRouterLocation(nextState.location)
  }

  return legacyRouterState
}

export const getDefaultLaunchedFromLibrary = (nextState) =>
  hasStoreEnabled() ? "/store/" : "/library/"

// Generic util for getting default launched from url
export const getDefaultLaunchedFromHome = (nextState) =>
  canGoHome() ? "/home/" : getDefaultLaunchedFromLibrary(nextState)

export const getInitialRedirect = (location) => {
  // enforce forward slash
  if (
    !location.pathname.endsWith("/") &&
    !location.pathname.endsWith(".html")
  ) {
    // found route without closing slash, redirect it
    return {
      ...location,
      pathname: `${location.pathname}/`
    }
  }

  if ("forceHardRefresh" in getQueryObject(location.search)) {
    return {
      ...location,
      // Remove the "forceHardRefresh" query param.
      search: new URLSearchParams(
        omit(getQueryObject(location.search), "forceHardRefresh")
      ).toString()
    }
  }

  // When in local dev, attempt to redirect user to config url if able.
  if (getIsLocalHost()) {
    const schoolQueryParam = getSchoolQueryParam()

    let { pathname, search, hash } = location

    if (schoolQueryParam) {
      // Always remove the ?school query param.
      const searchParams = new URLSearchParams(search)
      searchParams.delete("school")
      search = searchParams.toString()
      if (typeof schoolQueryParam === "string") {
        const localConfigURL = transoformLocalConfigURL(schoolQueryParam)
        // When schoolQueryParam is the school url, redirect to the local config url if able.
        if (localConfigURL.pathname !== "/") pathname = localConfigURL.pathname
        if (localConfigURL.search !== "") search = localConfigURL.search
        if (localConfigURL.hash !== "") hash = localConfigURL.hash
      }

      if (
        pathname !== location.pathname ||
        search !== location.search ||
        hash !== location.hash
      ) {
        return {
          pathname,
          search,
          hash
        }
      }
    }
  }

  const { is_authenticated } = window.App.getStore("auth").getState()

  const schoolInactiveRedirectRoute =
    window.App.getStore("navigation").request.getSchoolInactiveRedirectRoute(
      location
    )
  if (schoolInactiveRedirectRoute) {
    return schoolInactiveRedirectRoute
  }

  if (location.pathname !== "/") {
    // Apply stored route hash to location.
    const routeHash = sessionStorage.getItem("routeHash")
    if (routeHash && window.location.hash !== routeHash) {
      sessionStorage.removeItem("routeHash")
      // Unclear why, but taking this approach to updating the hash works,
      // while doing something like `return { ...location, hash: routeHash }` doesn't.
      location.hash = routeHash
      return { ...location }
    }

    return null
  }

  // root route redericts

  if (!is_authenticated) {
    // Ensure home page will be displayed if exists
    const homePage = window.App.getStore("school").request.getPageForURL(
      location.pathname
    )
    if (homePage && homePage.url.indexOf("http") === -1) {
      return homePage.url
    }
  }

  // Go to home location
  const redirectLocation =
    window.App.getStore("navigation").request.getHomeLocation()

  return redirectLocation
}

const toArray = (value) => (Array.isArray(value) ? value : [value])

// Routes of one card stack share a <Route> so the stack stays mounted while
// navigating between its cards.
const groupCardRoutes = (routes) => {
  const groups = new Map()
  return routes.reduce((items, route) => {
    const stackRoot = route.cards?.[0]
    if (!stackRoot) return [...items, [route]]
    if (groups.has(stackRoot)) {
      groups.get(stackRoot).push(route)
      return items
    }
    const group = [route]
    groups.set(stackRoot, group)
    return [...items, group]
  }, [])
}

/**
 * Convert route configs into <Route>s, rendered by both the background and
 * foreground route layers. Pass `registration` ({ key, priority }) to make the
 * routes available for classifying locations as pages or overlays.
 */
export const createRoutes = (routes, registration) => {
  // spread any child route that is an array so that childRoutes is a flat list
  routes = routes.reduce((routes, childRoute) => {
    if (Array.isArray(childRoute)) {
      return [...routes, ...childRoute]
    }

    return [...routes, childRoute]
  }, [])

  // ensure every route is provided with the closest primary
  // component relative to itself and parent routes
  const getPrimaryComponent = (route, parentPrimaryComponent) =>
    route.component ||
    (route.components && route.components.primary) ||
    parentPrimaryComponent

  function convertToRoutes(routes, parentPath = "", parentPrimaryComponent) {
    return groupCardRoutes(routes).map((group) => {
      const [route] = group
      const paths =
        group.length > 1
          ? group.map((route) => `${parentPath}${route.path}`)
          : toArray(route.path).map((path) => `${parentPath}${path}`)
      const hasChildRoutes =
        group.length === 1 && !!route.childRoutes && paths.length === 1
      const childRoutes = hasChildRoutes
        ? convertToRoutes(
            route.childRoutes,
            paths[0],
            getPrimaryComponent(route, parentPrimaryComponent)
          )
        : []
      const exact = !hasChildRoutes

      const element = (
        <Route
          key={paths.join("|")}
          path={paths.length === 1 ? paths[0] : paths}
          render={(props) => {
            const activeRoute =
              group.length > 1 ? group[paths.indexOf(props.match.path)] : route

            return (
              <>
                <AppRoute
                  {...props}
                  route={activeRoute}
                  component={getPrimaryComponent(
                    activeRoute,
                    parentPrimaryComponent
                  )}
                />
                {childRoutes.map((child) => child.element)}
              </>
            )
          }}
          exact={exact}
          strict
        />
      )

      return {
        element,
        registered: {
          paths,
          exact,
          kind: getRouteKind(route),
          routes: group,
          children: childRoutes.map((child) => child.registered)
        }
      }
    })
  }

  const converted = convertToRoutes(routes)

  if (registration) {
    registerRouteTable(
      registration.key,
      registration.priority,
      routes,
      converted.map((item) => item.registered)
    )
  }

  return converted.map((item) => item.element)
}

// Priority of each route table when classifying locations, matching the order
// the Switches try them in.
export const ROUTE_TABLE_PRIORITY = {
  app: 0,
  libraryStore: 1,
  library: 2,
  store: 3
}

// Use a React Router route as a CSS safe class name.
// Useful for styling route specific components (ex: Card stack modals)
export function getRouteClassName(routePattern = "/") {
  return routePattern
    .replace(/^\/|\/$/g, "") // remove leading/trailing slashes
    .split("/") // split into segments
    .map((segment) =>
      segment.startsWith(":")
        ? "_" + segment.slice(1) // make :param into _param
        : segment
    )
    .map((part) => part.replace(/[^a-zA-Z0-9_-]/g, "-")) // sanitize
    .map((part) => "--" + part) // prepend --
    .join("")
}
